import { Draggable } from "gsap/Draggable";
import { useGsap } from "@sazka/utils/gsap-register";
import { useVariatorHelpers, type VariatorArgs } from "../../product-page/components/variator/use-variator-helpers";
import { debounce } from "@sazka/utils/debounce";

const { gsap } = useGsap(Draggable);

interface Props {
    startValue: number;
    containerEl: HTMLElement | null;
    barEl: HTMLElement | null;
    badgeEl: HTMLElement | null;
    sliderElems: HTMLElement[] | null;
    snapPoints?: number[];
    initAnimationDelayMs?: number;
    updateBadgeCount: (count: number) => void;
}

interface Variator {
    destroy: () => void | null;
    setCurrentValue: (value: number) => void | null;
}

const useVariator = ({
    startValue,
    containerEl,
    barEl,
    badgeEl,
    sliderElems,
    snapPoints,
    updateBadgeCount,
}: Props): Variator => {
    // Props
    let currentStepNumber: number;
    const variatorArgs = { containerEl, badgeEl, snapPoints } satisfies VariatorArgs;
    const variatorHelper = useVariatorHelpers(variatorArgs);

    let isKilled = false;

    // Observer
    const observerConfig: MutationObserverInit = { attributes: true, childList: false, subtree: false };
    const observerCallback: MutationCallback = () => {
        const currentX = badgeEl.getBoundingClientRect().x - containerEl.getBoundingClientRect().x;
        currentStepNumber = variatorHelper.getDisplayStepNumber(currentX);

        gsap.set(barEl, { width: currentX + badgeEl.offsetWidth });
        updateBadgeCount(currentStepNumber);
    };

    const observer = new MutationObserver(observerCallback);
    observer.observe(badgeEl, observerConfig);
    const destroyController = new AbortController();

    gsap.set(badgeEl, {
        x: variatorHelper.points.value[snapPoints.indexOf(startValue)],
    });

    // Init Draggable
    const getDraggableOptions = (): Draggable.Vars => ({
        type: "x",
        bounds: containerEl,
        liveSnap: {
            x: variatorHelper.points.value,
            radius: 25,
        },
        onDrag: function () {
            currentStepNumber = variatorHelper.getDisplayStepNumber(this.endX);
            updateBadgeCount(currentStepNumber);
        },
    });

    let draggable = new Draggable(badgeEl, getDraggableOptions());

    // On resize
    async function resizeCallback() {
        if (isKilled) return;
        variatorHelper.resize();

        draggable.kill();
        draggable = new Draggable(badgeEl, getDraggableOptions());
        const currentX = variatorHelper.getXByStepNumber(currentStepNumber);

        gsap.set(badgeEl, { x: currentX });
    }

    const resizeCallbackDebounced = debounce(resizeCallback, 100);

    window.addEventListener("resize", resizeCallbackDebounced, { signal: destroyController.signal });

    const handleClick = (e: Event) => {
        const target = e.target as HTMLElement;

        const stepNumber = Number(target.dataset.badgeNumber) ?? 0;

        gsap.set(badgeEl, { x: variatorHelper.getXByStepNumber(stepNumber) });
    };

    sliderElems?.forEach((el) => {
        el.addEventListener("click", (e) => handleClick(e), { signal: destroyController.signal });
    });

    const destroy = () => {
        isKilled = true;
        observer.disconnect();
        draggable?.kill();
        destroyController?.abort();
    };

    const setCurrentValue = (value: number) => {
        currentStepNumber = value;
        gsap.set(badgeEl, { x: variatorHelper.getXByStepNumber(currentStepNumber) });
    };

    return {
        destroy,
        setCurrentValue,
    };
};

export { Variator, useVariator };
